import type { RemoteConfigKey } from "@unbox/api";
import { useFeatureFlagPayload } from "posthog-js/react";
import { z } from "zod";

// Remote config keys are prefixed `C-` and declared in `@unbox/api` alongside feature flags.
// Every key maps to a zod schema describing its payload shape; the hook validates the payload
// internally so callers get a parsed, typed result.
const bannerContent = z.object({ title: z.string().optional(), message: z.string().min(1) });

const remoteConfigSchemas = {
  // Per-workspace warning banner. Keyed by workspace id; the special `all` key is shown to every
  // workspace and takes precedence over any workspace-specific entry.
  "C-banner": z.record(z.string(), bannerContent),
  // Warning banner shown on unauthenticated pages, where there is no workspace to key by.
  "C-login-banner": bannerContent,
} as const satisfies Record<RemoteConfigKey, z.ZodTypeAny>;

export type { RemoteConfigKey };

export const useRemoteConfig = <K extends RemoteConfigKey>(key: K): z.infer<(typeof remoteConfigSchemas)[K]> | null => {
  const payload = useFeatureFlagPayload(key);

  let raw: unknown = payload;
  // localStorage override (JSON object keyed by config key), e.g.
  // {"C-banner": {"all": {"title": "Ongoing issue", "message": "..."}}}
  try {
    const overrides = JSON.parse(localStorage.getItem("remote_config") || "{}");
    if (key in overrides) raw = overrides[key];
  } catch {
    // ignore invalid JSON
  }

  // Indexing the registry by the generic `K` widens the parsed result to a union of all schema outputs,
  // so cast back to this key's precise type.
  const parsed = remoteConfigSchemas[key].safeParse(raw).data ?? null;
  return parsed as z.infer<(typeof remoteConfigSchemas)[K]> | null;
};
